import { AxiosError, AxiosResponse } from 'axios';
import { createAsyncThunk } from '@reduxjs/toolkit';

import { api } from 'api';
import { Messages } from 'config';
import { resetSessionId } from 'api/instance';
import { parseAxiosError } from 'utils/error';
import { AuthResponse, SsoCallbackPayload } from 'api/auth/types';
import { Role, AuthEmail, VerifyCode, VerificationType } from 'types';
import {
  clearAll,
  saveUser,
  getTokens,
  saveTokens,
  saveAuth0Id,
  clearAuth0Id,
  saveAuthStage,
  clearAuthStage,
} from 'utils/storage';

import { setLoading } from '../loader';
import { resetApiState } from '../../store';

import { types, AuthLogout } from './types';
import { clearAuth, setAuthError } from '.';

interface NormalizedAuthResponse {
  data: AuthResponse;
  isLoggedIn: boolean;
  mfaOptions?: VerificationType[];
  signupVerifications?: VerificationType[];
}

const normalizeAuthResponse = (
  authData?: AuthResponse
): NormalizedAuthResponse | { error: string } => {
  const {
    user,
    auth0Id,
    mfaToken,
    mfaOptions,
    accessToken,
    refreshToken,
    signupVerifications,
  } = authData || {};

  const isLoggedIn = Boolean(
    !signupVerifications?.length && !mfaOptions?.length && user
  );

  if (
    isLoggedIn &&
    user &&
    user.role !== Role.ADMIN &&
    user.role !== Role.PROJECT_ADMIN &&
    user.role !== Role.RESEARCH_ADMIN &&
    user.role !== Role.REPORT_MANAGER &&
    user.role !== Role.TENANT_ADMIN &&
    user.role !== Role.CONTENT_MANAGER &&
    user.role !== Role.LIBRARY_ADMIN
  ) {
    return { error: Messages.UNAUTHORIZED };
  }

  if (auth0Id) {
    saveAuth0Id(auth0Id);
  } else {
    clearAuth0Id();
  }

  saveTokens({
    refreshToken,
    accessToken: accessToken || mfaToken,
  });
  if (accessToken) {
    saveAuthStage('authenticated');
  } else if (mfaToken) {
    saveAuthStage('mfa');
  } else {
    clearAuthStage();
  }

  if (isLoggedIn && user) {
    saveUser(user);
  }

  return {
    isLoggedIn,
    mfaOptions,
    signupVerifications,
    data: authData ?? ({} as AuthResponse),
  };
};

export const login = createAsyncThunk(
  types.auth,
  async (payload: AuthEmail, { rejectWithValue }) => {
    try {
      resetSessionId();
      const data = await api.auth.login(payload);
      const authData = data?.data?.data;
      const result = normalizeAuthResponse(authData);
      if ('error' in result) {
        return rejectWithValue(result.error);
      }
      return result;
    } catch (error) {
      return rejectWithValue(parseAxiosError(error));
    }
  }
);

export const ssoCallback = createAsyncThunk(
  types.ssoCallback,
  async (payload: SsoCallbackPayload, { rejectWithValue }) => {
    try {
      const data = await api.auth.ssoCallback(payload);
      const authData = data?.data?.data;
      const result = normalizeAuthResponse(authData);
      if ('error' in result) {
        return rejectWithValue(result.error);
      }
      return result;
    } catch (error) {
      return rejectWithValue(parseAxiosError(error));
    }
  }
);

export const logout = createAsyncThunk(
  types.logout,
  async (values: AuthLogout, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      try {
        const { refreshToken } = getTokens();
        await api.auth.logout({ refreshToken });
      } catch (error: any) {
        console.error(error.message);
      }

      if (values.error) {
        const message = parseAxiosError(values.error);
        const axiosError = values.error as AxiosError;
        const restorePath = !(
          axiosError.code === '401' &&
          (axiosError.response?.data as any)?.error?.msg !==
            'Invalid authorization token'
        );
        dispatch(
          setAuthError({
            message,
            restorePath,
          })
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      resetSessionId();
      clearAll();
      dispatch(setLoading(false));
      dispatch(clearAuth());
      resetApiState(dispatch);
    }
    return {};
  }
);

export const checkEmailVerification = createAsyncThunk(
  types.checkEmailVerification,
  async (_payload, { rejectWithValue }) => {
    const handleError = (error: unknown) => {
      const errorMessage = parseAxiosError(error);
      return rejectWithValue(errorMessage);
    };

    try {
      const data = await api.auth.checkEmailVerification();
      const user = data.data.data.user;
      if (user) {
        saveUser(user);
        return { user, isLoggedIn: true };
      } else {
        return { isLoggedIn: false };
      }
    } catch (error) {
      return handleError(error);
    }
  }
);

export const verifyCodes = createAsyncThunk(
  types.verifyCodes,
  async (payload: VerifyCode[], { rejectWithValue }) => {
    const handleError = (error: unknown) => {
      const errorMessage = parseAxiosError(error);
      return rejectWithValue(errorMessage);
    };

    const successVerifiedTypes: VerificationType[] = [];
    const successfulResponses: AxiosResponse<any, any>[] = [];

    for (const item of payload) {
      try {
        const response = await api.auth.mfaVerify({
          code: item.code,
          type: item.type,
          oobCode: item.oobCode,
        });
        successfulResponses.push(response);
        successVerifiedTypes.push(item.type);
      } catch (error) {
        return handleError(error);
      }
    }

    const lastResponse = successfulResponses[successfulResponses.length - 1];

    const authData = lastResponse?.data?.data || {};
    const { user, accessToken, refreshToken, signupVerifications } = authData;

    const isLoggedIn = user !== undefined;

    if (accessToken && refreshToken) {
      saveTokens({ accessToken, refreshToken });
      saveAuthStage('authenticated');
      clearAuth0Id();
    }

    if (isLoggedIn) {
      saveUser(user);
    }

    return {
      isLoggedIn,
      data: authData,
      signupVerifications,
      successVerifiedTypes,
    };
  }
);
